"use client";

import { useEffect } from "react";
import { useParams, useRouter } from "next/navigation";
import Link from "next/link";
import { useQuery, useMutation } from "convex/react";
import { useAuth } from "@clerk/clerk-react";
import { api } from "../../../../convex/_generated/api";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  PluginIcon,
  DownloadIcon,
  StarIcon,
  StarFilledIcon,
  EyeIcon,
  TagIcon,
  ArrowRightIcon,
  FileIcon,
} from "@/components/icons";
import { MarkdownRenderer } from "@/components/ui/markdown-renderer";

function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function formatDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export default function PluginDetailPage() {
  const params = useParams();
  const router = useRouter();
  const { isSignedIn } = useAuth();
  const slug = params.slug as string;

  const plugin = useQuery(api.plugins.getBySlug, { slug });
  const hasStarred = useQuery(
    api.plugins.hasStarred,
    plugin && isSignedIn ? { pluginId: plugin._id } : "skip"
  );
  const downloadUrl = useQuery(
    api.plugins.getDownloadUrl,
    plugin ? { pluginId: plugin._id } : "skip"
  );

  const toggleStar = useMutation(api.plugins.toggleStar);
  const trackView = useMutation(api.plugins.trackView);
  const trackDownload = useMutation(api.plugins.trackDownload);

  // Track view on page load
  useEffect(() => {
    if (plugin && isSignedIn) {
      trackView({ pluginId: plugin._id });
    }
  }, [plugin?._id, isSignedIn, trackView]);

  const handleDownload = async () => {
    if (!plugin || !downloadUrl) return;

    // Track download
    if (isSignedIn) {
      await trackDownload({ pluginId: plugin._id });
    }

    // Build clean filename: "PluginName-v1.0.0.jar"
    const cleanName = plugin.name.replace(/[^a-zA-Z0-9._-]/g, "-");
    const fileName = `${cleanName}-v${plugin.version}.jar`;

    // Fetch as blob to bypass cross-origin download attribute restriction
    const response = await fetch(downloadUrl);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  };

  const handleStarClick = async () => {
    if (!plugin || !isSignedIn) return;
    await toggleStar({ pluginId: plugin._id });
  };

  if (plugin === undefined) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <Navbar />
        <main className="flex-1">
          <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
            <Skeleton className="h-12 w-1/2" />
            <Skeleton className="mt-4 h-6 w-1/4" />
            <Skeleton className="mt-8 h-64" />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (plugin === null) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <Navbar />
        <main className="flex-1">
          <div className="mx-auto max-w-4xl px-4 py-20 text-center sm:px-6 lg:px-8">
            <PluginIcon className="mx-auto h-16 w-16 text-muted" />
            <h1 className="mt-4 text-2xl font-bold text-foreground">
              Plugin Not Found
            </h1>
            <p className="mt-2 text-muted">
              The plugin you&apos;re looking for doesn&apos;t exist or has been
              removed.
            </p>
            <Link href="/plugins" className="mt-6 inline-block">
              <Button>
                <ArrowRightIcon className="mr-2 h-4 w-4 rotate-180" />
                Back to Plugins
              </Button>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Navbar />

      <main className="flex-1">
        <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="rounded-lg bg-primary/10 p-4 text-primary">
                <PluginIcon className="h-10 w-10" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground">
                  {plugin.name}
                </h1>
                <p className="mt-1 text-lg text-muted">
                  Version {plugin.version}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {isSignedIn && (
                <Button
                  variant="outline"
                  onClick={handleStarClick}
                  className={hasStarred ? "text-yellow-500" : ""}
                >
                  {hasStarred ? (
                    <StarFilledIcon className="mr-2 h-4 w-4" />
                  ) : (
                    <StarIcon className="mr-2 h-4 w-4" />
                  )}
                  {hasStarred ? "Starred" : "Star"}
                </Button>
              )}
              <Button onClick={handleDownload} disabled={!downloadUrl}>
                <DownloadIcon className="mr-2 h-4 w-4" />
                Download
              </Button>
            </div>
          </div>

          {/* Uploader Info */}
          <div className="mt-6 flex items-center gap-3">
            <Avatar className="h-8 w-8">
              <AvatarImage src={plugin.uploaderAvatar} />
              <AvatarFallback>
                {plugin.uploaderName.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <span className="text-sm text-muted">
              Uploaded by <span className="text-foreground">{plugin.uploaderName}</span>
            </span>
          </div>

          {/* Tags */}
          {plugin.tags.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2">
              {plugin.tags.map((tag) => (
                <Link key={tag} href={`/plugins?tag=${tag}`}>
                  <Badge variant="secondary" className="cursor-pointer">
                    <TagIcon className="mr-1 h-3 w-3" />
                    {tag}
                  </Badge>
                </Link>
              ))}
            </div>
          )}

          {/* Stats & Info Grid */}
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardContent className="flex items-center gap-3 p-4">
                <div className="rounded-lg bg-blue-500/10 p-2 text-blue-500">
                  <EyeIcon className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">
                    {plugin.viewCount}
                  </p>
                  <p className="text-sm text-muted">Views</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="flex items-center gap-3 p-4">
                <div className="rounded-lg bg-green-500/10 p-2 text-green-500">
                  <DownloadIcon className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">
                    {plugin.downloadCount}
                  </p>
                  <p className="text-sm text-muted">Downloads</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="flex items-center gap-3 p-4">
                <div className="rounded-lg bg-yellow-500/10 p-2 text-yellow-500">
                  <StarIcon className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">
                    {plugin.starCount}
                  </p>
                  <p className="text-sm text-muted">Stars</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="flex items-center gap-3 p-4">
                <div className="rounded-lg bg-purple-500/10 p-2 text-purple-500">
                  <FileIcon className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">
                    {formatFileSize(plugin.fileSize)}
                  </p>
                  <p className="text-sm text-muted">File Size</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Description */}
          <Card className="mt-8">
            <CardHeader>
              <CardTitle>Description</CardTitle>
            </CardHeader>
            <CardContent>
              <MarkdownRenderer content={plugin.description} />
            </CardContent>
          </Card>

          {/* File Info */}
          <Card className="mt-4">
            <CardHeader>
              <CardTitle>File Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted">File Name</span>
                <span className="font-mono text-foreground">
                  {plugin.fileName}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted">File Size</span>
                <span className="text-foreground">
                  {formatFileSize(plugin.fileSize)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted">Uploaded</span>
                <span className="text-foreground">
                  {formatDate(plugin.createdAt)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted">Last Updated</span>
                <span className="text-foreground">
                  {formatDate(plugin.updatedAt)}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Back Button */}
          <div className="mt-8">
            <Link href="/plugins">
              <Button variant="outline">
                <ArrowRightIcon className="mr-2 h-4 w-4 rotate-180" />
                Back to Plugins
              </Button>
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
